import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import {
  CheckCircle2,
  Clock,
  Loader2,
  Smartphone,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  CancelPaymentRequest,
  PaymentStatus,
} from '../api';
import AuthGuard from '../AuthGuard';
import { Button } from '../components/ui/button';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { useSubscription } from '../SubscriptionProvider';
import { runtimeConfig } from '../platform/runtime';
import {
  buildNativeStripeCheckoutReturnUrl,
  clearPendingStripeCheckout,
  getPendingStripeCheckoutPaymentId,
  persistPendingStripeCheckout,
  StripeCheckoutReturnOutcome,
} from '../platform/stripeCheckout';

type StripeReturnState =
  | 'checking'
  | 'paid'
  | 'cancelled'
  | 'failed'
  | 'expired'
  | 'timeout'
  | 'technical'
  | 'missing';

const pollIntervalMs = 3000;
const pollTimeoutMs = 60000;

const mapPaymentStatus = (status: PaymentStatus | undefined): StripeReturnState | null => {
  switch (status) {
    case PaymentStatus.Paid:
      return 'paid';
    case PaymentStatus.Cancelled:
      return 'cancelled';
    case PaymentStatus.Failed:
      return 'failed';
    case PaymentStatus.Expired:
      return 'expired';
    default:
      return null;
  }
};

const NativeCheckoutReturnBridge: React.FC<{
  outcome: StripeCheckoutReturnOutcome;
  search: string;
}> = ({ outcome, search }) => {
  const { t } = useTranslation();
  const deepLink = useMemo(
    () => buildNativeStripeCheckoutReturnUrl(outcome, search),
    [outcome, search],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      window.location.assign(deepLink);
    }, 50);

    return () => window.clearTimeout(timer);
  }, [deepLink]);

  return (
    <IonPage>
      <IonContent fullscreen className="bg-gradient-to-br from-slate-50 via-stone-100 to-gray-100">
        <div className="flex min-h-screen items-center justify-center px-4 py-8">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-lg">
            <Smartphone className="mx-auto mb-4 h-10 w-10 text-indigo-600" />
            <h1 className="text-xl font-semibold text-slate-900">
              {t('stripeCheckoutReturnOpenAppTitle')}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {t('stripeCheckoutReturnOpenAppDescription')}
            </p>
            <Button asChild className="mt-6 w-full bg-indigo-600 text-white hover:bg-indigo-700">
              <a href={deepLink}>{t('stripeCheckoutReturnOpenAppAction')}</a>
            </Button>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

const AuthenticatedStripeCheckoutReturn: React.FC<{
  outcome: StripeCheckoutReturnOutcome;
  paymentId: string | null;
}> = ({ outcome, paymentId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const api = useAstroApiClient();
  const { refresh } = useSubscription();
  const [returnState, setReturnState] = useState<StripeReturnState>(
    paymentId ? 'checking' : 'missing',
  );
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!paymentId) {
      setReturnState('missing');
      return undefined;
    }

    persistPendingStripeCheckout(paymentId);
    setReturnState('checking');

    let disposed = false;
    let timer: number | undefined;
    const startedAt = Date.now();

    const complete = (nextState: StripeReturnState) => {
      if (disposed) {
        return;
      }

      if (
        nextState === 'paid' ||
        nextState === 'cancelled' ||
        nextState === 'failed' ||
        nextState === 'expired'
      ) {
        clearPendingStripeCheckout(paymentId);
      }

      setReturnState(nextState);

      if (nextState === 'paid') {
        void refresh();
      }
    };

    const schedulePoll = () => {
      if (disposed) {
        return;
      }

      if (Date.now() - startedAt >= pollTimeoutMs) {
        complete('timeout');
        return;
      }

      timer = window.setTimeout(() => {
        void poll();
      }, pollIntervalMs);
    };

    const poll = async (): Promise<void> => {
      try {
        const response = await api.apiPaymentsReconcile(paymentId);
        const terminalState = mapPaymentStatus(response.status);

        if (terminalState) {
          complete(terminalState);
          return;
        }

        schedulePoll();
      } catch {
        schedulePoll();
      }
    };

    const resume = async () => {
      if (outcome === 'cancel') {
        try {
          const response = await api.apiPaymentsCancel(
            new CancelPaymentRequest({
              paymentId,
              reason: 'stripe-checkout-return',
            }),
          );
          const terminalState = mapPaymentStatus(response.status);

          if (terminalState) {
            complete(terminalState);
            return;
          }
        } catch {
          // The provider may already have completed; refresh its authoritative state below.
        }
      }

      await poll();
    };

    void resume();

    return () => {
      disposed = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [api, outcome, paymentId, refresh, retryKey]);

  const stateContent: Record<
    StripeReturnState,
    { title: string; description: string }
  > = {
    checking: {
      title: t('stripeCheckoutReturnCheckingTitle'),
      description: t('stripeCheckoutReturnCheckingDescription'),
    },
    paid: {
      title: t('stripeCheckoutReturnPaidTitle'),
      description: t('stripeCheckoutReturnPaidDescription'),
    },
    cancelled: {
      title: t('stripeCheckoutReturnCancelledTitle'),
      description: t('stripeCheckoutReturnCancelledDescription'),
    },
    failed: {
      title: t('stripeCheckoutReturnFailedTitle'),
      description: t('stripeCheckoutReturnFailedDescription'),
    },
    expired: {
      title: t('stripeCheckoutReturnExpiredTitle'),
      description: t('stripeCheckoutReturnExpiredDescription'),
    },
    timeout: {
      title: t('stripeCheckoutReturnTimeoutTitle'),
      description: t('stripeCheckoutReturnTimeoutDescription'),
    },
    technical: {
      title: t('stripeCheckoutReturnTechnicalTitle'),
      description: t('stripeCheckoutReturnTechnicalDescription'),
    },
    missing: {
      title: t('stripeCheckoutReturnMissingTitle'),
      description: t('stripeCheckoutReturnMissingDescription'),
    },
  };
  const content = stateContent[returnState];
  const isChecking = returnState === 'checking';
  const isSuccess = returnState === 'paid';
  const canRetryStatus = returnState === 'timeout' || returnState === 'technical';

  return (
    <IonPage>
      <IonContent fullscreen className="bg-gradient-to-br from-slate-50 via-stone-100 to-gray-100">
        <div className="flex min-h-screen items-center justify-center px-4 py-8">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-lg">
            {isChecking ? (
              <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-indigo-600" />
            ) : isSuccess ? (
              <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-green-600" />
            ) : returnState === 'timeout' ? (
              <Clock className="mx-auto mb-4 h-10 w-10 text-amber-600" />
            ) : (
              <XCircle className="mx-auto mb-4 h-10 w-10 text-red-600" />
            )}

            <h1 className="text-xl font-semibold text-slate-900">{content.title}</h1>
            <p className="mt-2 text-sm text-slate-600">{content.description}</p>

            {!isChecking && (
              <div className="mt-6 space-y-3">
                {canRetryStatus && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setRetryKey((value) => value + 1)}
                  >
                    {t('stripeCheckoutReturnCheckAgain')}
                  </Button>
                )}
                <Button
                  className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
                  onClick={() => history.replace('/subscription/pro')}
                >
                  {isSuccess
                    ? t('stripeCheckoutReturnContinue')
                    : t('stripeCheckoutReturnBackToCheckout')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

const StripeCheckoutReturn: React.FC<{
  outcome: StripeCheckoutReturnOutcome;
}> = ({ outcome }) => {
  const location = useLocation();
  const browserSearch =
    typeof window === 'undefined' ? '' : window.location.search;
  const returnSearch = location.search || browserSearch;
  const query = useMemo(() => new URLSearchParams(returnSearch), [returnSearch]);
  const resolvedPaymentId =
    query.get('payment_id') ?? getPendingStripeCheckoutPaymentId();
  const paymentIdRef = useRef(resolvedPaymentId);

  if (resolvedPaymentId) {
    paymentIdRef.current = resolvedPaymentId;
  }

  const paymentId = paymentIdRef.current;
  const requiresNativeBridge =
    query.get('return_target') === 'native' &&
    !runtimeConfig.isNativePlatform;

  if (requiresNativeBridge) {
    return (
      <NativeCheckoutReturnBridge
        outcome={outcome}
        search={returnSearch}
      />
    );
  }

  return (
    <AuthGuard>
      <AuthenticatedStripeCheckoutReturn
        key={paymentId ?? 'missing-payment-reference'}
        outcome={outcome}
        paymentId={paymentId}
      />
    </AuthGuard>
  );
};

export default StripeCheckoutReturn;
